<template>
	<div>
		<div v-show="fermenters.length == 0" style="padding: 40px 0; text-align: center">
			<h3 v-if="selectedTankGroup == 'favourites'">There are currently no fermenters favourited.</h3>
			<h3 v-else>There are currently no fermenters in this tank group.</h3>
			<h3><a href="#" @click.prevent="showAllFermenters()">Show all fermenters <i class="fa fa-angle-right"></i></a></h3>
		</div>
		<div class="row row-clear-sm-6-md-4-lg-3 ferments">
			<div v-for="fermenter in fermenters" class="ferment-grid col-sm-6 col-md-4 col-lg-3">
				<div class="box box-ferment" v-bind:class="{'box-ferment-empty' : !fermenter.currentFerment}">
					<div class="box-header">
						<div class="pull-right box-tools">
							<input id="favourite_{{fermenter.id}}" type="checkbox" class="favourite" v-model="fermenter.favourite">
							<label for="favourite_{{fermenter.id}}"></label>
						</div>
						<h3 class="box-title">
							<a v-link="{name: 'ferments.index', params: { fermenterId: fermenter.id, fermentId: fermenter.currentFerment ? fermenter.currentFerment.data.id : 0 }}">
								<i class="bg-green fontello-waves"></i> <span class="text-green" v-if="simulatorSetup.dashboard_tank_name">{{fermenter.name}}</span>
							</a>
						</h3>
					</div>

					<div class="box-body">
						<div v-if="fermenter.currentFerment" class="row">
							<div class="col-md-12">
								<h3 v-if="fermenter.currentFerment && simulatorSetup.dashboard_wine_name">{{fermenter.currentFerment.data.wine_name}}</h3>
							</div>
							<div class="col-md-12">
								<div class="text-small" v-if="fermenter.currentFerment.data.varietal && simulatorSetup.dashboard_varietal"><small>{{fermenter.currentFerment.data.varietal}}</small></div>
								<div v-if="fermenter.currentFerment.data.blend_code && simulatorSetup.dashboard_blend_code"><small>{{fermenter.currentFerment.data.blend_code}}</small></div>
								<div v-if="fermenter.currentFerment.data.batch_code && simulatorSetup.dashboard_batch_code"><small>{{fermenter.currentFerment.data.batch_code}}</small></div>
							</div>
						</div>

						<h3 v-if="!fermenter.currentFerment" class="text-center">Empty</h3>

						<ferment-gauge :fermenter="fermenter"></ferment-gauge>
					</div>
					<div class="alerts gauge-alerts" v-if="fermenter.currentFerment">
						<div v-if="fermenter.currentFerment.data.blendComponents.data.length > 0" class="alert alert-default">
							Blend – No simulation available
						</div>
						<template v-else>
							<div if="fermenter.currentFerment.data.simulated && !fermentNotEnoughData(fermenter.currentFerment.data)">
								<div v-if="fermenter.currentFerment.data.gauge.data.status == 10" class="alert alert-info" role="alert">
									<i class="fa fa-exclamation-triangle"></i> Low Range On Spec <quote :message="lowRangeOnSpecQuote" :class="'white'"></quote>
								</div>
								<div v-if="fermenter.currentFerment.data.gauge.data.status == 20" class="alert alert-danger" role="alert">
									<i class="fa fa-exclamation-triangle"></i> High Range On Spec <quote :message="highRangeOnSpecQuote" :class="'white'"></quote>
								</div>
								<div v-if="fermenter.currentFerment.data.gauge.data.status == 30" class="alert alert-info" role="alert">
									<i class="fa fa-exclamation-triangle"></i> Sluggish Fermentation <quote :message="sluggishFermentationQuote" :class="'white'"></quote>
								</div>
								<div v-if="fermenter.currentFerment.data.gauge.data.status == 40" class="alert alert-danger" role="alert">
									<i class="fa fa-exclamation-triangle"></i> Rapid Fermentation <quote :message="rapidFermentQuaote" :class="'white'"></quote>
								</div>
							</div>
							<div v-if="!fermentNotEnoughData(fermenter.currentFerment.data) && !fermenter.currentFerment.data.simulated" class="alert alert-warning" role="alert">
								<i class="fa fa-exclamation-triangle"></i> Simulation Out of Sync <quote :message="simulationOutOfSyncQuote"></quote>
							</div>
							<div v-if="fermentNotEnoughData(fermenter.currentFerment.data)" class="alert alert-warning" role="alert">
								<i class="fa fa-exclamation-triangle"></i> Not Enough Data <quote :message="notEnoughtDataQuote"></quote>
							</div>
							<div v-if="fermenterIsStuck(fermenter)" class="alert alert-warning" role="alert">
								<i class="fa fa-exclamation-triangle"></i> Ferment Stuck
							</div>
							<div v-if="fermenter.currentFerment.data.gauge.data.prediction_score > 25 && fermenter.currentFerment.data.simulated" class="alert alert-warning" role="alert">
								<i class="fa fa-exclamation-triangle"></i> Poor Simulation <quote :message="poorSimulationQuote"></quote>
							</div>
						</template>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
	import { simulatorSetup } from 'vx/getters'
	import FermentGauge from 'components/ferment/gauge'
	import FermenterHelpers from 'utils/fermenters'

	export default {
		components: {
			FermentGauge
		},

		data() {
			return {
				sluggishFermentationQuote: `
					This ferment is predicted to run behind schedule, based on the desired duration entered.
					To get it back on track, consider warming it up, aeration, or adding nutrients.
				`,
				rapidFermentQuaote: `
					This ferment is predicted to finish ahead of schedule, based on the desired duration entered.
					To get it back on track, consider cooling it down.
				`,
				poorSimulationQuote: `
					<p>The simulator is struggling to lock onto this ferment. There may be several reasons for this.</p>
					<ul>
						<li>Check for incorrectly entered data. You may be able to see bad data points on the graph.</li>
						<li>Ensure all actions performed have been entered (DAP, mixing events, dilution, etc).</li>
						<li>Ensure the temperature measurements are an accurate representation of the tank.</li>
						<li>The ferment may be suffering from a complex issue like micro nutrient deficiency or competition from other yeast or bacteria and requires specific winemaker attention.</li>
						<li>
							Lastly, no model is perfect and the ferment may be outside the range of the current model’s predictive capability.
							As more data is gathered, the model will continue to improve.
						</li>
					</ul>
				`,
				notEnoughtDataQuote: `
					There is not enough data yet to run the simulation.
					The simulator needs at least 4 measurements to run.
					If you have this, you may be missing something else.
					Open the ferments to see what’s missing.
				`,
				simulationOutOfSyncQuote: `
					The ferment has been changed but a simulation has not yet been run.
					You can open this ferment and click Run Simulation to update it manually, or leave it and it will update itself automatically in the background.
				`,
				lowRangeOnSpecQuote:`
					This ferment predicted to run behind schedule. Only the lower end of the confidence interval is within spec.
					Consider warming it up, aeration or adding nutrients.
				`,
				highRangeOnSpecQuote: `
					This ferment predicted to run ahead schedule.
					Only the upper end of the confidence interval is within spec.
					Consider cooling it down.
				`
			};
		},

		vuex: {
			getters: {
				simulatorSetup
			}
		},

		props: ['fermenters', 'selectedTankGroup'],

		methods: {
			...FermenterHelpers,

			showAllFermenters() {
				this.selectedTankGroup = 'all';
			},

			fermenterIsStuck(fermenter) {
				return ! this.fermentNotEnoughData(fermenter.currentFerment.data) && parseFloat(fermenter.currentFerment.data.gauge.data.predicted_duration) == 99;
			}
		}
	}
</script>

<style scoped>
	.fa-question-circle {
		float: right !important;
		margin-top: 5px !important;
	}

	.fa-question-circle.white {
		color: white;
	}
</style>



// WEBPACK FOOTER //
// fermenters.vue?79d97b4d